import { Empty, Select, Space, Tag, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { RequestLogEntry } from "shell/console/types";

import {
  useSandboxAdminRequestLogs,
  useSandboxAdminUsers,
} from "shell/console/api/sandboxAdmin/hooks";
import { SandboxAdminTableLayout } from "shell/console/features/shared/SandboxAdminTableLayout";
import { formatDateToCustomFormat } from "shell/platform/utils/dateFormatter";
import { extractRestErrorMessage } from "shell/platform/utils/utils";

import { toUserSelectOptions } from "./serializers";

const { Text } = Typography;

const methodColors: Record<string, string> = {
  GET: "green",
  POST: "blue",
  PUT: "orange",
  PATCH: "purple",
  DELETE: "red",
  HEAD: "cyan",
  OPTIONS: "cyan",
};

const STATUS_COLORS = [
  { min: 500, color: "red" },
  { min: 400, color: "orange" },
  { min: 300, color: "cyan" },
];

const getStatusColor = (statusCode: number): string =>
  STATUS_COLORS.find(({ min }) => statusCode >= min)?.color ?? "green";

export const AuditLog = () => {
  const { t } = useTranslation("common");
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(
    undefined,
  );

  const usersQuery = useSandboxAdminUsers();
  const { data, isLoading, error, refetch, isFetching } =
    useSandboxAdminRequestLogs(selectedUserId);

  const logs = data?.logs ?? [];
  const users = usersQuery.data?.users ?? [];
  const userOptions = toUserSelectOptions(users);

  const usersErrorMessage = usersQuery.error
    ? extractRestErrorMessage(usersQuery.error) ||
      usersQuery.error.message ||
      t("errors.unknown")
    : null;
  const logsErrorMessage = error
    ? extractRestErrorMessage(error) || error.message || t("errors.unknown")
    : null;
  const queryErrorMessage = usersErrorMessage ?? logsErrorMessage;

  const handleRetry = () => {
    if (usersErrorMessage) {
      usersQuery.refetch();
      return;
    }
    refetch();
  };

  const toolbar = (
    <Space>
      <Text strong>{t("labels.user")}</Text>
      <Select
        allowClear
        showSearch
        placeholder={t(
          "components.sandboxAdmin.auditLog.selectUserPlaceholder",
        )}
        loading={usersQuery.isLoading}
        options={userOptions}
        value={selectedUserId}
        onChange={setSelectedUserId}
        style={{ minWidth: 320 }}
        data-testid="select-user"
      />
    </Space>
  );

  const columns = [
    {
      title: t("labels.date"),
      dataIndex: "timestamp",
      key: "timestamp",
      render: (timestamp: number) => formatDateToCustomFormat(timestamp),
    },
    {
      title: t("labels.method"),
      dataIndex: "method",
      key: "method",
      render: (method: string) => (
        <Tag color={methodColors[method] ?? "default"}>{method}</Tag>
      ),
    },
    {
      title: t("labels.path"),
      dataIndex: "path",
      key: "path",
      render: (path: string, record: RequestLogEntry) => (
        <Text code>
          {path}
          {record.query_string ? `?${record.query_string}` : ""}
        </Text>
      ),
    },
    {
      title: t("labels.status"),
      dataIndex: "status_code",
      key: "statusCode",
      render: (statusCode: number) => (
        <Tag color={getStatusColor(statusCode)}>{statusCode}</Tag>
      ),
    },
    {
      title: t("components.sandboxAdmin.auditLog.duration"),
      dataIndex: "duration_ms",
      key: "durationMs",
      render: (durationMs: number) => `${durationMs} ms`,
    },
  ];

  return (
    <SandboxAdminTableLayout
      title={t("components.sandboxAdmin.auditLog.title")}
      subtitle={t("components.sandboxAdmin.auditLog.subtitle")}
      toolbar={toolbar}
      data={logs}
      columns={columns}
      loading={isLoading}
      rowKey={record => `${record.user_id}-${record.timestamp}`}
      errorMessage={queryErrorMessage}
      onRetry={handleRetry}
      retryLoading={isFetching || usersQuery.isFetching}
      renderEmpty={() => (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("components.sandboxAdmin.auditLog.noUserSelected")}
        />
      )}
      refreshTestId="refresh-logs"
      testId="sandbox-audit-log-table"
    />
  );
};
